import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import ResponsiveAppBar from "../../components/ResponsiveAppBar";
import backgroundIcon from "../../assets/images/background_icon.svg";
import { useTheme, Button, Typography } from "@mui/material";
import OutlinedLabelledTextField from "../../components/OutlinedLabelledTextfield";
import MessageCard from "./components/MessageCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { resetPassword } from "../../api/reset.request";
import { toast } from "react-toastify";

function EmailVerificationSuccessful() {
  const theme = useTheme();
  const [createPassword, setCreatePassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const emailParam = queryParams.get("email");
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [location]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleCreatePasswordChange = (event) => {
    setCreatePassword(event.target.value);
  };

  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };

  const handleSavePassword = async () => {
    if (createPassword.length < 8) {
      console.error("Password must be at least 8 characters long");
      toast.error("Password must be at least 8 characters long");
      return;
    }

    if (createPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      if (!email) {
        console.error("Email is missing");
        return;
      }
      await resetPassword(email, createPassword);
      setPasswordUpdated(true);
    } catch (error) {
      console.error("Error resetting password:", error);
    }
  };

  return (
    <>
      <ResponsiveAppBar />
      <div
        style={{
          backgroundImage: `url(${backgroundIcon})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right",
          minHeight: "75vh",
          display: "flex",
          justifyContent: "center",
          alignItems: { md: "center" },
        }}
      >
        <Container
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {passwordUpdated ? (
            <MessageCard
              loaderComponent={
                <CheckCircleIcon
                  sx={{
                    fontSize: 60,
                    mt: 4,
                    color: theme.palette.green.text,
                  }}
                />
              }
              primaryText={"Password Updated"}
              secondaryText={`Your password has been updated successfully. Redirecting to login page.`}
              buttonText={"Back to Log in"}
              buttonHref={"/login"}
            />
          ) : (
            <>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  mt: { xs: 7, md: 10 },
                  fontWeight: "bold",
                  fontSize: { xs: "1.6rem" },
                }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body1"
                gutterBottom
                sx={{ mb: { md: 4 }, color: "grey.text" }}
              >
                Enter your email linked to your account and <br /> we'll send
                you a link to reset your password.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mt: 2,
                  width: "100%",
                  maxWidth: "sm",
                }}
              >
                <OutlinedLabelledTextField
                  id="createPassword"
                  label="Create Password"
                  placeholder="Create a password"
                  type={showPassword ? "text" : "password"}
                  value={createPassword}
                  onChange={handleCreatePasswordChange}
                  width="75%"
                  eye={true}
                  showPassword={showPassword}
                  togglefunction={togglePasswordVisibility}
                  helperText={"Password must be at least 8 characters long."}
                />
                <OutlinedLabelledTextField
                  id="confirmPassword"
                  label="Confirm Password"
                  placeholder="Re-enter your password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  width="75%"
                  eye={true}
                  showPassword={showConfirmPassword}
                  togglefunction={toggleConfirmPasswordVisibility}
                />
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  sx={{
                    mt: 3,
                    borderRadius: "8px",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    textTransform: "none",
                    width: { md: "75%" },
                  }}
                  onClick={handleSavePassword}
                  disabled={
                    !createPassword || createPassword !== confirmPassword
                  }
                >
                  Update Password
                </Button>
              </Box>
            </>
          )}
        </Container>
      </div>
    </>
  );
}

export default EmailVerificationSuccessful;
